import { CalendarOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Layout, notification, Skeleton } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useInterval } from "src/core/hooks/useInterval";
import { orderService } from "src/core/services/orderService";
import { LeftSidebar } from "src/modules/leftSidebar/LeftSidebar";
import { OrdersTable } from "src/modules/ordersTable/OrdersTable";
import { ContentPageHeader } from "src/modules/pageHeader/PageHeader";
import { Order } from "src/types/types";

type Props = {
  pageMode?: "all-orders" | "todays-orders";
};

const { Content } = Layout;

export const UserOrdersPage = (props: Props) => {
  const { pageMode } = props;

  const [loading, setLoading] = useState(true);
  const [userOrders, setUserOrders] = useState<Order[]>([]);

  const { t } = useTranslation();
  const history = useHistory();

  const refreshOrders = useCallback(async () => {
    setLoading(true);

    const response =
      pageMode === "todays-orders"
        ? await orderService.getTodaysUserOrders()
        : await orderService.getUserOrders();
    setUserOrders(response);

    setLoading(false);
  }, [pageMode]);

  useEffect(() => {
    (async () => {
      await refreshOrders();

      setLoading(false);
    })();
  }, [refreshOrders]);

  const refreshOrdersSilently = async () => {
    const response = await orderService.getNewUserOrders(
      userOrders[0]?.id ?? 0
    );

    if (response.length > 0) {
      const mergedOrders = response.concat(userOrders);
      setUserOrders(mergedOrders);

      response.forEach((ord: Order) => {
        notification.success({
          message: t("label.newOrder"),
          description: `${t("message.orderNumber")}: ${ord.orderNumber}`,
          onClick: () => {},
          top: 90,
          duration: 2400,
        });
      });
    }
  };

  const onEditSuccess = async () => {
    const response = await orderService.getUserOrders();
    setUserOrders(response);
  };

  var buttons = [];

  if (pageMode === "todays-orders") {
    buttons.push(
      <Button
        onClick={() => history.push("/admin/orders")}
        disabled={loading}
        key="todays-orders-button"
      >
        <CalendarOutlined /> {t("button.allOrders")} {"->"}
      </Button>
    );
  } else {
    buttons.push(
      <Button
        onClick={() => history.push("/admin/orders/today")}
        disabled={loading}
        key="todays-orders-button"
      >
        <CalendarOutlined /> {t("button.onlyTodaysOrders")} {"->"}
      </Button>
    );
  }

  buttons.push(
    <Button
      onClick={async () => {
        await refreshOrders();
      }}
      disabled={loading}
      key="refresh-button"
    >
      <ReloadOutlined /> {t("button.refresh")}
    </Button>
  );

  useInterval(() => {
    refreshOrdersSilently();
  }, 60000);

  return (
    <Layout>
      <LeftSidebar page="orders" />
      <Content style={{ textAlign: "center", padding: "5px" }}>
        <Layout>
          <ContentPageHeader pageTitle={t("title.orders")} buttons={buttons} />
          <Skeleton active loading={loading}>
            {userOrders.length === 0 && pageMode !== "todays-orders" && (
              <Content style={{ textAlign: "left", paddingLeft: 25 }}>
                <h3>{t("message.ordersIntro")}</h3>
              </Content>
            )}
            <Content style={{ padding: 25 }}>
              <OrdersTable orders={userOrders} onEditSuccess={onEditSuccess} />
            </Content>
          </Skeleton>
        </Layout>
      </Content>
    </Layout>
  );
};
